// FILE: TagsSingleComponent.jsx
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom"; // ⬅️ NEW
import styles from "./TagsSingleComponent.module.css";

import { slugify } from "../../../../../utils/firebase/core/collections";
import {
  segmentsFromPath,
  TAG_PATH_SEP,
  normalizeFullPath,
} from "../../UploadDataComponent/utils/tagUtils";

import { AccountsAPI, TagsAPI, TransactionsAPI, RulesAPI } from "../../../../../utils/firebase";

/**
 * Tag detail + inline editing.
 * - Edit leaf name
 * - Edit parent chain (each segment inline with optional type-ahead)
 * - Save triggers subtree move (descendants follow)
 *
 * Optional prop `allTagPaths` (array<string>) enables type-ahead suggestions for parent segments.
 */
export default function TagsSingleComponent({ tag, userEmail, allTagPaths = [] }) {
  const navigate = useNavigate(); // ⬅️ NEW

  // ---------- Derive original leaf + parents from the tag ----------
  const { leafName, parentSegs, parentPath, origFullPath } = useMemo(() => {
    const raw = normalizeFullPath(tag?.fullPath || tag?.name || "");
    const segs = segmentsFromPath(raw);
    const leaf = segs.length ? segs[segs.length - 1] : "";
    const pSegs = segs.slice(0, -1);
    const pPath = pSegs.join(TAG_PATH_SEP);
    return { leafName: leaf, parentSegs: pSegs, parentPath: pPath, origFullPath: raw };
  }, [tag]);

  const canEdit = !!tag && !!userEmail;

  // ---------- Build segment suggestions from allTagPaths (optional) ----------
  const segmentSuggestions = useMemo(() => {
    const set = new Set();
    for (const fp of allTagPaths || []) {
      for (const s of segmentsFromPath(fp)) set.add(s);
    }
    return Array.from(set);
  }, [allTagPaths]);

  // ---------- Modes ----------
  const [editingName, setEditingName] = useState(false);
  const [editingParent, setEditingParent] = useState(false);
  const isEditing = editingName || editingParent;

  // ---------- Form state ----------
  const [name, setName] = useState(leafName);
  React.useEffect(() => setName(leafName), [leafName]);

  const [parentParts, setParentParts] = useState(parentSegs);
  React.useEffect(() => setParentParts(parentSegs), [parentSegs]);

  const [createMissingAncestors, setCreateMissingAncestors] = useState(true);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [okMsg, setOkMsg] = useState("");

  // Type-ahead state (which parent input is focused)
  const [focusIdx, setFocusIdx] = useState(-1);

  // ---------- Helpers ----------
  const cleanedParentParts = useMemo(
    () => parentParts.map((s) => s.trim()).filter(Boolean),
    [parentParts]
  );

  const newParentPath = useMemo(
    () => (cleanedParentParts.length ? cleanedParentParts.join(TAG_PATH_SEP) : ""),
    [cleanedParentParts]
  );

  const newFullPath = useMemo(() => {
    const leaf = String(name || "").trim();
    if (!leaf) return "";
    return normalizeFullPath(newParentPath ? `${newParentPath}${TAG_PATH_SEP}${leaf}` : leaf);
  }, [newParentPath, name]);

  const hasNameChange = name !== leafName;
  const hasParentChange =
    cleanedParentParts.join("\u0001") !== parentSegs.join("\u0001");
  const hasAnyChange = hasNameChange || hasParentChange;

  const disableSave = !isEditing || !hasAnyChange || !newFullPath || newFullPath === origFullPath || saving;

  const onAddParentSegment = () => setParentParts((p) => [...p, ""]);
  const onRemoveParentSegment = (idx) =>
    setParentParts((p) => p.filter((_, i) => i !== idx));
  const onChangeParentSeg = (idx, val) =>
    setParentParts((p) => p.map((s, i) => (i === idx ? val : s)));

  const onCancelEdits = () => {
    setName(leafName);
    setParentParts(parentSegs);
    setEditingName(false);
    setEditingParent(false);
    setErr(null);
    setOkMsg("");
    setFocusIdx(-1);
  };

  // ---------- Type-ahead filtering ----------
  const activeQuery = focusIdx >= 0 ? (parentParts[focusIdx] || "") : "";
  const filteredSuggestions = useMemo(() => {
    const q = activeQuery.trim().toLowerCase();
    if (!q) return segmentSuggestions.slice(0, 8);
    const starts = [];
    const contains = [];
    for (const item of segmentSuggestions) {
      const s = String(item || "");
      const L = s.toLowerCase();
      if (L.startsWith(q)) starts.push(s);
      else if (L.includes(q)) contains.push(s);
    }
    return [...starts, ...contains].slice(0, 8);
  }, [activeQuery, segmentSuggestions]);

  const pickSuggestion = (idx, val) => {
    onChangeParentSeg(idx, val);
    setFocusIdx(-1);
  };

  // ---------- Save (subtree move if parent or name changed) ----------
  const onSave = async () => {
    if (!canEdit || !tag?.id || disableSave) return;
    setSaving(true);
    setErr(null);
    setOkMsg("");

    try {
      if (typeof TagsAPI.moveUserTagSubtree === "function") {
        await TagsAPI.moveUserTagSubtree({
          id: tag.id,
          newParentPath: newParentPath,
          newLeafName: String(name || "").trim(),
          createMissingAncestors,
          dryRun: false,
        });
      } else {
        console.warn(
          "[Tags] moveUserTagSubtree missing; falling back to updateUserTag (no descendants will move)."
        );
        await TagsAPI.updateUserTag({
          id: tag.id,
          updates: {
            name: String(name || "").trim(),
            fullPath: newFullPath,
            slug: slugify(newFullPath),
          },
        });
      }

      setOkMsg("Tag updated.");
      setEditingName(false);
      setEditingParent(false);
      setFocusIdx(-1);
    } catch (e) {
      console.error("[Tags] save error:", e);
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Search transactions for this tag ----------
  const onSearchTx = () => {
    // Use the leaf name so it matches “in any level” with your current filter logic.
    const query = (leafName || tag?.fullPath || tag?.name || "").trim();
    if (!query) return;

    const sp = new URLSearchParams();
    sp.set("tag", query);

    // Navigate to the canonical Transactions page with tag filter applied
    navigate({ pathname: "/app/transactions", search: sp.toString() });
  };

  if (!tag) return <div className="text-muted small">Select a tag to view & edit.</div>;

  return (
    <div className={styles.TagsSingleComponent}>
      <div className={styles.headerRow}>
        <h6 className="mb-0">Tag Details</h6>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onSearchTx}
            type="button"
            title="Search transactions with this tag"
          >
            Search transactions
          </button>

          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setEditingParent(true)}
            type="button"
            disabled={!canEdit}
            title={!canEdit ? "Sign in to edit" : "Edit parent chain"}
          >
            Edit parent
          </button>

          <button
            className="btn btn-primary btn-sm"
            onClick={() => setEditingName(true)}
            type="button"
            disabled={!canEdit}
            title={!canEdit ? "Sign in to edit" : "Edit tag name"}
          >
            Edit name
          </button>
        </div>
      </div>

      <div className={styles.body}>
        {/* --- Leaf name --- */}
        <div className="mb-3">
          <div className={styles.kvLabel}>Tag name</div>
          {!editingName ? (
            <div className={styles.kvValue}>
              <code className={styles.tagMono}>{leafName || "—"}</code>
            </div>
          ) : (
            <input
              type="text"
              className="form-control form-control-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ fontFamily: "var(--pp-font-mono)", color: "var(--pp-tag-color)" }}
              placeholder="Leaf name"
              onFocus={() => setEditingName(true)}
            />
          )}
        </div>

        {/* --- Parent path (editable per segment with type-ahead) --- */}
        <div className="mb-3">
          <div className={styles.kvLabel}>Parent path</div>

          {!editingParent ? (
            parentSegs?.length ? (
              <div className={styles.parentCrumbs}>
                {parentSegs.map((p, i) => (
                  <span key={`${p}-${i}`} className={styles.crumb}>
                    <span className={styles.crumbMono}>{p}</span>
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-muted small">— none —</div>
            )
          ) : (
            <>
              <div className={styles.parentEditor}>
                {parentParts.length === 0 && (
                  <div className="text-muted small mb-1">No parent — this will be a root tag.</div>
                )}

                {parentParts.map((seg, idx) => (
                  <div key={`seg-${idx}`} className={styles.segRow}>
                    <span className={styles.sepGlyph}>---</span>
                    <div className={styles.typeaheadWrap}>
                      <input
                        type="text"
                        className={`form-control form-control-sm ${styles.segInput}`}
                        value={seg}
                        onChange={(e) => onChangeParentSeg(idx, e.target.value)}
                        onFocus={() => setFocusIdx(idx)}
                        onBlur={() => {
                          setTimeout(() => setFocusIdx((cur) => (cur === idx ? -1 : cur)), 120);
                        }}
                        placeholder={`Level ${idx + 1}`}
                        autoComplete="off"
                        spellCheck="false"
                      />
                      {focusIdx === idx && filteredSuggestions.length > 0 && (
                        <div className={styles.suggestBox} role="listbox">
                          {filteredSuggestions.map((opt) => (
                            <button
                              key={`${idx}-${opt}`}
                              type="button"
                              className={styles.suggestItem}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => pickSuggestion(idx, opt)}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary btn-sm ${styles.segBtn}`}
                      onClick={() => onRemoveParentSegment(idx)}
                      title="Remove this level"
                    >
                      ×
                    </button>
                  </div>
                ))}

                <div className={styles.addRow}>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={onAddParentSegment}
                  >
                    + Add parent level
                  </button>
                </div>

                {/* polished option panel */}
                <div className={styles.optionPanel}>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="pp-create-missing-anc"
                      checked={createMissingAncestors}
                      onChange={(e) => setCreateMissingAncestors(e.target.checked)}
                    />
                    <label htmlFor="pp-create-missing-anc" className={`form-check-label ${styles.optionLabel}`}>
                      Create missing ancestors if they don’t exist
                    </label>
                  </div>

                  <div className={styles.optionNote}>
                    Reparenting will move <strong>this tag and all of its descendants</strong>.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* --- Full path & current path --- */}
        <div className="mb-2">
          <div className={styles.kvLabel}>Preview full path</div>
          <div className={styles.kvValue}>
            <code className={styles.tagMono}>{newFullPath || "—"}</code>
          </div>

          <div className={styles.currentRow}>
            <span className={styles.currentLabel}>Current</span>
            <code className={`${styles.tagMono} ${styles.currentMono}`}>{origFullPath}</code>
          </div>
        </div>

        {/* --- Actions (only in edit mode) --- */}
        {isEditing && (
          <div className="d-flex gap-2 mt-3">
            <button
              className="btn btn-success btn-sm"
              onClick={onSave}
              type="button"
              disabled={disableSave}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={onCancelEdits}
              type="button"
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        )}

        {/* --- Feedback --- */}
        {err && <div className="text-danger small mt-2">{err}</div>}
        {okMsg && <div className="text-success small mt-2">{okMsg}</div>}

        {/* --- Read-only extras --- */}
        <hr className="my-3" />
        <div className="mb-2">
          <div className={styles.kvLabel}>Slug</div>
          <div className={styles.kvValue}>
            <code>{tag.slug}</code>
          </div>
        </div>
      </div>
    </div>
  );
}

TagsSingleComponent.propTypes = {
  tag: PropTypes.object,
  userEmail: PropTypes.string,
  /** Optional: array of fullPath strings for type-ahead suggestions */
  allTagPaths: PropTypes.arrayOf(PropTypes.string),
};
TagsSingleComponent.defaultProps = {
  tag: null,
  userEmail: null,
  allTagPaths: [],
};
